import { ApiError, ApiNetworkError, authStart, authVerify } from '@cortex/web-api/clients/web/api';
import { Wordmark } from '@cortex/web-api/ui/web/Wordmark';
import { type FormEvent, useState } from 'react';

type Step = 'phone' | 'code';

interface Props {
  onVerified: () => void;
}

export function LoginScreen({ onVerified }: Props) {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePhoneSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const trimmed = phone.trim();
    if (!/^\+[1-9]\d{1,14}$/.test(trimmed)) {
      setError('Use international format — for example +14155551234.');
      return;
    }
    setBusy(true);
    try {
      const res = await authStart(trimmed);
      if (res.status === 'sent') {
        setStep('code');
      } else if (res.status === 'not_recognized') {
        setError("We don't have your number on file. Reach out to your property manager.");
      } else if (res.status === 'rate_limited') {
        setError('Too many tries. Wait an hour and try again.');
      }
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleCodeSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Enter the 6-digit code from the text.');
      return;
    }
    setBusy(true);
    try {
      const res = await authVerify(phone.trim(), trimmedCode);
      if (res.status === 'verified') {
        onVerified();
        return;
      }
      if (res.status === 'wrong_code') {
        const remaining = res.remainingAttempts ?? 0;
        setError(
          remaining > 0
            ? `That code didn't match. ${remaining} ${remaining === 1 ? 'try' : 'tries'} left.`
            : "That code didn't match.",
        );
      } else if (res.status === 'locked') {
        setError('Too many wrong codes. Restart from your phone number.');
        setStep('phone');
        setCode('');
      } else if (res.status === 'expired_or_unknown') {
        setError('That code expired or was already used. Request a new one.');
        setStep('phone');
        setCode('');
      }
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <header className="app-header">
        <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
        <Wordmark />
      </header>
      <main className="app-main">
        <div className="center-stack">
          <div className="card" style={{ padding: 'var(--space-8)', maxWidth: 420, width: '100%' }}>
            {step === 'phone' ? (
              <form onSubmit={handlePhoneSubmit} className="stack">
                <div className="stack-tight">
                  <span className="label">step 1 of 2</span>
                  <h2 className="h3" style={{ color: 'var(--ink)' }}>
                    Your phone number
                  </h2>
                  <p className="field-help">
                    We'll text a 6-digit code to confirm it's you. Use the same number on file with
                    your property manager.
                  </p>
                </div>
                <div className="stack-tight">
                  <label className="field-label" htmlFor="phone">
                    Phone (international)
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+14155551234"
                    className="field-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={busy}
                    required
                  />
                </div>
                {error && <p className="field-error">{error}</p>}
                <button type="submit" className="btn-primary" disabled={busy}>
                  {busy ? 'Sending…' : 'Send code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleCodeSubmit} className="stack">
                <div className="stack-tight">
                  <span className="label">step 2 of 2</span>
                  <h2 className="h3" style={{ color: 'var(--ink)' }}>
                    Enter the code
                  </h2>
                  <p className="field-help">
                    We sent a 6-digit code to <strong>{phone}</strong>. It expires in 5 minutes.
                  </p>
                </div>
                <div className="stack-tight">
                  <label className="field-label" htmlFor="code">
                    Code
                  </label>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    className="field-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
                    disabled={busy}
                    required
                    style={{
                      letterSpacing: '0.4em',
                      fontVariantNumeric: 'tabular-nums',
                      textAlign: 'center',
                      fontSize: '24px',
                    }}
                  />
                </div>
                {error && <p className="field-error">{error}</p>}
                <button type="submit" className="btn-primary" disabled={busy}>
                  {busy ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => {
                    setStep('phone');
                    setCode('');
                    setError(null);
                  }}
                >
                  Use a different number
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <footer className="app-footer">powered by acel·cortex</footer>
    </div>
  );
}

function messageForError(err: unknown): string {
  if (err instanceof ApiNetworkError) return "Couldn't reach our system. Check your connection.";
  if (err instanceof ApiError) {
    if (err.status === 400) return "That input wasn't valid. Try again.";
    if (err.status === 404) return 'This page is misconfigured. Contact your property manager.';
    return 'Something went wrong on our end. Try again in a moment.';
  }
  return 'Something went wrong. Try again.';
}
